import { cn, Skeleton } from '@mntn/ui-components'
import type { ReactNode } from 'react'
import { useEffect, useRef } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'

interface SearchResultsListProps<T> {
  results: T[]
  renderItem: (item: T) => ReactNode
  hasNextPage?: boolean
  fetchNextPage?: () => void
  isFetching?: boolean
  isFetchingNextPage?: boolean
  status?: 'pending' | 'success' | 'error'
  skeleton?: () => ReactNode
}

export function SearchResultsList<T>({
  results,
  renderItem,
  hasNextPage = false,
  fetchNextPage = () => {},
  isFetching,
  isFetchingNextPage,
  status,
  skeleton,
}: SearchResultsListProps<T>) {
  const containerRef = useRef<HTMLUListElement>(null)

  const isBusy = Boolean(status === 'pending' || isFetching || isFetchingNextPage)

  // Disable scrolling when fetching the next page
  useEffect(() => {
    if (!containerRef.current) return

    if (isFetchingNextPage) {
      // Store the current scroll position
      const scrollPosition = containerRef.current.scrollTop

      // Apply a class to prevent scrolling
      containerRef.current.style.overflowY = 'hidden'
      containerRef.current.style.position = 'relative'

      // Ensure we maintain the same scroll position
      containerRef.current.scrollTop = scrollPosition
    } else {
      // Re-enable scrolling when fetching is complete
      if (containerRef.current.style.overflowY === 'hidden') {
        containerRef.current.style.overflowY = 'auto'
        containerRef.current.style.position = ''
      }
    }
  }, [isFetchingNextPage])

  const [sentryRef] = useInfiniteScroll({
    loading: Boolean(isFetching || isFetchingNextPage),
    hasNextPage,
    onLoadMore: fetchNextPage,
    // When there is an error, we stop infinite loading.
    // It can be reactivated by setting "error" state as undefined.
    disabled: status === 'error',
    // `rootMargin` is passed to `IntersectionObserver`.
    // We can use it to trigger 'onLoadMore' when the sentry comes near to become
    // visible, instead of becoming fully visible on the screen.
    rootMargin: '0px 0px 200px 0px',
  })

  const sentry = hasNextPage ? <div ref={sentryRef} className="h-1" /> : null

  // Create list items directly
  const listItems = results.map((item, index) => <li key={index}>{renderItem(item)}</li>)

  // Create loading indicator directly
  const loadingIndicator = isFetchingNextPage ? (
    <div className="flex flex-col gap-1">
      <SkeletonRows skeleton={skeleton} />
    </div>
  ) : null

  return (
    <ul
      ref={containerRef}
      className={cn('flex flex-col overflow-auto', !skeleton && 'bg-sub-surface-bg')}
      aria-label="Search results"
      data-testid="search-results-list"
      aria-busy={isBusy ? 'true' : 'false'}
    >
      {status === 'pending' ? <SkeletonRows skeleton={skeleton} /> : listItems}
      {sentry}
      {loadingIndicator}
    </ul>
  )
}

const SkeletonRows = ({ skeleton }: { skeleton?: () => ReactNode }) => {
  return (
    <div className="flex flex-col gap-1" data-testid="search-results-list-skeleton-rows">
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={index}>{skeleton ? skeleton() : <Skeleton className="h-20 w-full" />}</div>
      ))}
    </div>
  )
}
